import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  IBackgroundImagesResponse,
  IVideoOptionsResponse,
} from '../components/tools/dpdg/modals/background-settings-modal';

export interface IFileInfo {
  id: string;
  filename: string;
  size: number;
  mimeType: string;
}

export interface ISpecialization {
  id: string | number;
  title: string;
  sort: number | null;
}

export interface ISpecializationsResponse {
  data: ISpecialization[];
}

export interface IAppSettings {
  defaults_dpdg_speed: number;
  base_price: string;
  base_duration: number;
  defaults_dpdg_size: number;
  defaults_dpdg_background: string;
}

export interface ICloudsHelperQuestion {
  id: string;
  sort: number;
  title: string;
  formatted_text: string;
}

export interface ICloudsHelperQuestionsResponse {
  data: ICloudsHelperQuestion[];
}

const directusDomain =
  ((import.meta.env.VITE_REACT_APP_DIRECTUS_DOMAIN as string | undefined) ?? 'https://d-t1.zingizi.pro').replace(
    /\/$/,
    '',
  );

const baseUrl = directusDomain;

export const directusApi = createApi({
  reducerPath: 'directusApi',
  baseQuery: fetchBaseQuery({ baseUrl }),
  tagTypes: ['Videos', 'BackgroundImages', 'Files', 'UserPsyCategories', 'AppSettings', 'CloudsHelperQuestions'],
  endpoints: (build) => ({
    getVideos: build.query<IVideoOptionsResponse, void>({
      query: () => '/items/dpdg_settings_video',
      providesTags: ['Videos'],
    }),

    getFileInfo: build.query<IFileInfo, string>({
      query: (id) => `/files/${id}`,
      providesTags: (result, error, id) => [{ type: 'Files', id }],
    }),

    getBackgroundImages: build.query<IBackgroundImagesResponse, void>({
      query: () => '/items/dpdg_settings_background_images',
      providesTags: ['BackgroundImages'],
    }),

    getAssetThumbnail: build.query<string, string>({
      query: (id) => `/assets/${id}?key=thumbnail`,
    }),

    getAssetFull: build.query<string, string>({
      query: (id) => `/assets/${id}`,
    }),

    getSpecializations: build.query<ISpecializationsResponse, void>({
      query: () => ({
        url: '/items/specialization',
        params: {
          fields: 'id,title,sort',
          sort: 'sort',
        },
      }),
      providesTags: ['UserPsyCategories'],
    }),

    getAppSettings: build.query<IAppSettings, void>({
      query: () => ({
        url: '/items/app_settings',
      }),
      transformResponse: (response: { data: Record<string, string> }) => {
        const raw = response.data;

        return {
          defaults_dpdg_speed: parseFloat(raw.defaults_dpdg_speed),
          base_price: raw.base_price,
          base_duration: parseInt(raw.base_duration),
          defaults_dpdg_size: parseInt(raw.defaults_dpdg_size),
          defaults_dpdg_background: raw.defaults_dpdg_background,
        };
      },
      providesTags: ['AppSettings'],
    }),

    getCloudsHelperQuestions: build.query<ICloudsHelperQuestionsResponse, void>({
      query: () => '/items/tools_clouds_helper_questions',
      providesTags: ['CloudsHelperQuestions'],
    }),
  }),
});

export const {
  useGetVideosQuery,
  useGetFileInfoQuery,
  useGetBackgroundImagesQuery,
  useGetAssetThumbnailQuery,
  useGetAssetFullQuery,
  useGetSpecializationsQuery,
  useGetAppSettingsQuery,
  useGetCloudsHelperQuestionsQuery,
} = directusApi;
